import React, { memo, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Route, Switch, useParams } from "react-router-dom";

import { SaleAction, setCurrentLivestockSaleId } from "actions";

import SecurityTrimmedRoute from "components/Authorization/SecurityTrimmedRoute";
import LoadingSpinner from "components/LoadingSpinner";
import MessageBox from "components/MessageBox";
import SingleWeighView from "components/SingleWeighView";

import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";
import { Settings } from "constants/settings";

import { getSaleyardAuctionRoute } from "lib/navigation";

import {
  getActiveRole,
  getCurrentSale,
  getCurrentSaleyard,
  getIsFetchingSales,
  getSaleById,
  getSetting,
  selectRoleCurrentDeployments,
} from "selectors";

import { useMountEffect } from "hooks";

import { AuctionRoutes } from "./AuctionRoutes";
import { LivestockSale } from "./LivestockSale";

// A livestock sale.
const LivestockSales = () => {
  const { saleyard, saleId } = useParams();
  const livestockSaleId = Number(saleId);

  const isFetchingSales = useSelector(getIsFetchingSales);

  const sale = useSelector(getCurrentSale);

  const selectedSaleExists = useSelector(state =>
    Boolean(getSaleById(livestockSaleId)(state)),
  );

  const dispatch = useDispatch();
  const activeRoleSlug = useSelector(getActiveRole).slug;
  const allSalesLoaded = useSelector(getSetting(Settings.allSalesLoaded));

  useEffect(() => {
    if (!selectedSaleExists && !allSalesLoaded) {
      dispatch(SaleAction.requestChunked({ fetchAll: true }));
    }
  }, [selectedSaleExists, allSalesLoaded, sale, dispatch]);

  useMountEffect(() => {
    // On unmounting, clear the sale, so we don't do changes since.
    return () => {
      dispatch(setCurrentLivestockSaleId(null, false));
    };
  });

  useEffect(() => {
    // Fetch data specific to this sale, if it exists when the sale or role changes.
    if (selectedSaleExists) {
      dispatch(setCurrentLivestockSaleId(livestockSaleId, false));
    }
  }, [dispatch, livestockSaleId, activeRoleSlug, selectedSaleExists]);
  if (!sale) {
    if (isFetchingSales) {
      return (
        <MessageBox>
          <LoadingSpinner subjectName="Sales" size={24} />
        </MessageBox>
      );
    } else {
      return (
        <MessageBox>
          Livestock sale could not be found. This may be because the sale does
          not exist (it may have been deleted), or you do not have sufficient
          access to the {saleyard} saleyard. Create a new sale or select an
          existing sale from the menu above.
        </MessageBox>
      );
    }
  }

  return (
    <Switch>
      {/* Auction routes - don't go through livestocksale as we don't want
        the header/sidebar/etc */}
      <SecurityTrimmedRoute
        objectArraySelector={selectRoleCurrentDeployments}
        permissionRequired={DeploymentPermission.featureAuction}
        path={`${getSaleyardAuctionRoute(":saleyard", ":saleId")}/round`}
        component={AuctionRoutes}
      />

      <SecurityTrimmedRoute
        objectSelector={getCurrentSaleyard}
        permissionRequired={SaleyardPermission.featureSingleWeigh}
        path={`${getSaleyardAuctionRoute(":saleyard", ":saleId")}/single-weigh`}
        component={SingleWeighView}
      />

      {/* Normal routes through sales - /overview /consignment-cards, etc, */}
      <Route
        path={`${getSaleyardAuctionRoute(":saleyard", ":saleId")}/:section?`}
        component={LivestockSale}
      />
    </Switch>
  );
};

export default memo(LivestockSales);
